import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, ShieldCheck, FileText, Calendar, Printer, Hash } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function QuoteAgreementSignPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({ signer_full_name: "", signer_title: "", signer_email: "", accepted_terms: false, accepted_monthly_review: false });
  const [submitting, setSubmitting] = useState(false);
  const [signed, setSigned] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data: d } = await axios.get(`${API}/api/quote-agreement/${token}`);
        setData(d);
        if (d.status === "signed" || d.status === "active_customer") setSigned(true);
      } catch (e) {
        setError(e?.response?.data?.detail || "Aftalen blev ikke fundet eller er udløbet.");
      } finally { setLoading(false); }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.signer_full_name.trim()) return setError("Indtast venligst dit fulde navn");
    if (!form.accepted_terms || !form.accepted_monthly_review) return setError("Begge accept-felter skal være tikket af");
    setError(null); setSubmitting(true);
    try {
      await axios.post(`${API}/api/quote-agreement/${token}/sign`, form);
      setSigned(true);
      const { data: d } = await axios.get(`${API}/api/quote-agreement/${token}`);
      setData(d);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      setError(e?.response?.data?.detail || "Der opstod en fejl ved signering.");
    } finally { setSubmitting(false); }
  };

  if (loading) return <Shell><div className="text-center py-12">Indlæser aftale...</div></Shell>;
  if (error && !data) return <Shell><div className="p-8 border-2 border-red-300 bg-red-50 text-red-800" data-testid="sign-error">{error}</div></Shell>;
  if (!data) return null;

  const monthlyEstimate = (data.agreed_price_per_pkg_dkk || 0) * (data.agreed_monthly_volume || 0);

  return (
    <Shell>
      {signed ? (
        <div data-testid="sign-success">
          <div className="border-2 border-emerald-500 bg-emerald-50 p-8 mb-6">
            <div className="flex items-center gap-3 mb-4">
              <CheckCircle2 size={32} className="text-emerald-600" />
              <h1 className="text-3xl font-bold text-emerald-900">Aftalen er underskrevet</h1>
            </div>
            <p className="text-emerald-800 mb-4">
              Tak {data.signed_by_full_name || ""}. Aftalen er nu digitalt underskrevet og vi går straks i gang med opsætningen af jeres erhvervskonto.
            </p>
            <div className="bg-white border border-emerald-300 p-4 text-sm">
              <div><span className="text-stone-500">Reference:</span> <strong>{data.reference}</strong></div>
              <div><span className="text-stone-500">Firma:</span> <strong>{data.company_name}</strong></div>
              <div><span className="text-stone-500">Underskrevet:</span> <strong>{data.signed_at_iso?.replace("T", " ").slice(0, 19)}</strong></div>
              <div><span className="text-stone-500">Underskrevet af:</span> <strong>{data.signed_by_full_name}</strong>{data.signed_by_title ? ` · ${data.signed_by_title}` : ""}</div>
              {data.signer_email && <div><span className="text-stone-500">Underskriver-email:</span> <strong>{data.signer_email}</strong></div>}
            </div>
            <p className="text-sm text-emerald-700 mt-4">
              Vi sender en bekræftelse til {data.contact_email} og en kopi af aftalen indenfor 1 arbejdsdag.
            </p>
          </div>

          {/* Snapshot bevis-blok (immutable) */}
          {data.agreement_snapshot_hash && (
            <div className="border-2 border-stone-800 bg-stone-50 p-6 mb-6" data-testid="agreement-snapshot-proof">
              <h2 className="text-lg font-bold text-brand-dark mb-3 flex items-center gap-2">
                <ShieldCheck size={18} /> Signatur-bevis (immutable)
              </h2>
              <table className="w-full text-sm">
                <tbody>
                  <RowK k="Aftaleversion" v={<code className="font-mono text-xs bg-white px-2 py-1 border border-stone-300">{data.agreement_snapshot_version}</code>} />
                  <RowK k="SHA-256 hash" v={
                    <code className="font-mono text-xs bg-white px-2 py-1 border border-stone-300 break-all" data-testid="agreement-snapshot-hash">
                      {data.agreement_snapshot_hash}
                    </code>
                  } />
                  {data.signer_ip && <RowK k="IP-adresse" v={<code className="font-mono text-xs">{data.signer_ip}</code>} />}
                  {data.signer_user_agent && <RowK k="User-Agent" v={<code className="font-mono text-xs break-all">{data.signer_user_agent}</code>} />}
                </tbody>
              </table>
              <div className="flex gap-3 mt-4 flex-wrap">
                <a href={`${API}/api/quote-agreement/${token}/snapshot.html`}
                   target="_blank" rel="noreferrer"
                   className="inline-flex items-center gap-2 bg-brand-dark text-white px-4 py-2 text-sm font-semibold hover:bg-black"
                   data-testid="view-snapshot-html">
                  <Printer size={16} /> Se / print underskrevet aftale
                </a>
                <span className="inline-flex items-center gap-2 text-xs text-stone-500">
                  <Hash size={14} /> Enhver ændring i aftaleteksten ville ændre hash&apos;en.
                </span>
              </div>
            </div>
          )}

          {/* Render det gemte snapshot inline for gennemsyn */}
          {data.agreement_snapshot_html && (
            <div className="border border-stone-300 bg-white p-2" data-testid="agreement-snapshot-render">
              <iframe
                title="Signeret aftale (snapshot)"
                srcDoc={data.agreement_snapshot_html}
                className="w-full"
                style={{ minHeight: "800px", border: 0 }}
                sandbox=""
              />
            </div>
          )}
        </div>
      ) : (
        <>
          <div className="mb-8">
            <div className="kicker mb-2">Erhvervsaftale — digital signering</div>
            <h1 className="text-3xl sm:text-4xl font-bold text-brand-dark" data-testid="sign-h1">Prisaftale for {data.company_name}</h1>
            <p className="text-brand-text2 mt-3">Reference: <span className="font-mono">{data.reference}</span></p>
          </div>

          {error && <div className="mb-6 p-4 border-2 border-red-300 bg-red-50 text-red-800" data-testid="sign-error">{error}</div>}

          {/* Offer summary */}
          <section className="bg-stone-50 border border-stone-300 p-6 mb-6" data-testid="agreement-summary">
            <h2 className="text-lg font-bold text-brand-dark mb-4 flex items-center gap-2"><FileText size={18} /> Tilbuddets indhold</h2>
            <table className="w-full text-sm">
              <tbody>
                <RowK k="Firma" v={`${data.company_name} (CVR ${data.cvr})`} />
                <RowK k="Kontakt" v={`${data.contact_name} · ${data.contact_email}`} />
                <RowK k="Afhentning" v={`${data.pickup_address} (${data.pickup_region})`} />
                <RowK k="Leveringsområde" v={data.delivery_area} />
                <RowK k="Aftalt månedlig volumen" v={`${data.agreed_monthly_volume} pakker`} />
                <RowK k="Pris pr. pakke (ex moms)" v={<strong>{(data.agreed_price_per_pkg_dkk || 0).toFixed(2)} DKK</strong>} />
                <RowK k="Estimeret månedlig faktura" v={`${monthlyEstimate.toLocaleString("da-DK")} DKK ex moms`} />
                <RowK k="Gyldig til" v={(data.offer_valid_until_iso || "").slice(0, 10)} />
              </tbody>
            </table>
          </section>

          {/* Sprint 2R-6E — Betalingsbetingelser */}
          {(data.payment_method || data.billing_email) && (
            <section className="bg-white border border-stone-300 p-6 mb-6" data-testid="agreement-payment">
              <h2 className="text-lg font-bold text-brand-dark mb-4">Betalingsbetingelser</h2>
              <table className="w-full text-sm">
                <tbody>
                  {data.billing_email && <RowK k="Faktura-email" v={data.billing_email} />}
                  {data.payment_method_label_da && (
                    <RowK k="Betalingsmetode" v={<strong>{data.payment_method_label_da}</strong>} />
                  )}
                  {data.invoice_frequency_label_da && (
                    <RowK k="Fakturafrekvens" v={data.invoice_frequency_label_da} />
                  )}
                  {typeof data.payment_terms_days === "number" && (
                    <RowK k="Betalingsfrist" v={`${data.payment_terms_days} dage netto`} />
                  )}
                </tbody>
              </table>
              {data.payment_method === "automatic_collection" && (
                <div className="mt-4 p-3 border-2 border-amber-300 bg-amber-50 text-amber-900 text-sm" data-testid="agreement-autocollect-notice">
                  <strong>Automatisk opkrævning afventer godkendelse.</strong><br />
                  Automatisk opkrævning aktiveres først, når mandatet er underskrevet og registreret af EMA Kurer.
                  Indtil da faktureres efter den aftalte fakturafrekvens.
                </div>
              )}
            </section>
          )}

          {/* Service flow */}
          <section className="bg-white border border-stone-300 p-6 mb-6" data-testid="agreement-flow">
            <h2 className="text-lg font-bold text-brand-dark mb-4">Prisaftalen omfatter</h2>
            <ol className="list-decimal list-inside space-y-1 text-sm text-brand-text2">
              <li>Afhentning fra kundens lager/adresse</li>
              <li>Transport til EMA Kurers HUB</li>
              <li>Scanning og sortering</li>
              <li>Levering til modtager</li>
              <li>Track &amp; trace + leveringsbevis</li>
              <li>Månedlig fakturering</li>
              <li>Månedlig prisopfølgning</li>
            </ol>
          </section>

          {/* Clauses — full collaboration agreement (14 sections) */}
          <section className="bg-white border border-stone-300 p-6 mb-6" data-testid="agreement-clauses">
            <h2 className="text-lg font-bold text-brand-dark mb-4 flex items-center gap-2"><ShieldCheck size={18} /> Aftalevilkår</h2>
            {data.agreement_sections && data.agreement_sections.length > 0 ? (
              <div className="space-y-5" data-testid="agreement-sections">
                {data.agreement_sections.map((sec, si) => (
                  <div key={si} className="border-l-2 border-brand-red pl-4" data-testid={`agreement-section-${si + 1}`}>
                    <h3 className="font-display font-bold text-brand-dark text-base mb-2">
                      §{si + 1} · {sec.title}
                    </h3>
                    <ol className="text-sm text-brand-text2 space-y-2 list-none">
                      {(sec.clauses || []).map((c, ci) => (
                        <li key={ci} className="flex gap-2">
                          <span className="text-brand-red font-mono text-xs mt-0.5 shrink-0">{si + 1}.{ci + 1}</span>
                          <span className="leading-relaxed">{c}</span>
                        </li>
                      ))}
                    </ol>
                  </div>
                ))}
              </div>
            ) : (
              // Legacy fallback (backward-compat if agreement_sections isn't sent)
              <ul className="space-y-3 text-sm text-brand-text2">
                {(data.agreement_clauses || []).map((c, i) => (
                  <li key={i} className="flex gap-2"><span className="text-brand-red font-bold">§{i + 1}</span><span>{c}</span></li>
                ))}
              </ul>
            )}
          </section>

          {/* EMA party block */}
          <section className="bg-white border border-stone-300 p-6 mb-6 text-sm">
            <div className="font-bold text-brand-dark mb-2">Leverandør</div>
            <div><strong>{data.ema_kurer?.name}</strong> · CVR {data.ema_kurer?.cvr}</div>
            <div>Direktør: {data.ema_kurer?.director}</div>
            <div className="italic">{data.ema_kurer?.kontakt}</div>
          </section>

          {/* Signing form */}
          <form onSubmit={submit} className="bg-brand-dark text-white p-6 mb-6" data-testid="sign-form">
            <h2 className="text-xl font-bold mb-5 flex items-center gap-2"><Calendar size={20} /> Digital signering</h2>
            <div className="grid sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-semibold mb-1">Dit fulde navn *</label>
                <input value={form.signer_full_name} onChange={(e) => setForm({ ...form, signer_full_name: e.target.value })}
                  required className="w-full p-3 text-brand-dark" data-testid="sign-full-name" />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1">Titel (valgfri)</label>
                <input value={form.signer_title} onChange={(e) => setForm({ ...form, signer_title: e.target.value })}
                  placeholder="fx Direktør, CEO, Indkøbschef" className="w-full p-3 text-brand-dark" data-testid="sign-title" />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-semibold mb-1">Underskriver-email (valgfri)</label>
                <input type="email" value={form.signer_email} onChange={(e) => setForm({ ...form, signer_email: e.target.value })}
                  placeholder={data.contact_email || "din@email.dk"} className="w-full p-3 text-brand-dark" data-testid="sign-email" />
                <p className="text-xs text-white/60 mt-1">Bruges udelukkende til revisions- og signatur-bevis. Standard: {data.contact_email}</p>
              </div>
            </div>
            <div className="space-y-3 text-sm mb-5">
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" checked={form.accepted_terms} onChange={(e) => setForm({ ...form, accepted_terms: e.target.checked })}
                  className="mt-1" data-testid="sign-accept-terms" />
                <span>Jeg accepterer aftalens vilkår, herunder pris og serviceflow ovenfor.</span>
              </label>
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" checked={form.accepted_monthly_review} onChange={(e) => setForm({ ...form, accepted_monthly_review: e.target.checked })}
                  className="mt-1" data-testid="sign-accept-review" />
                <span>Jeg accepterer, at prisen kan justeres fra <strong>næste måned</strong> baseret på faktisk pakkevolumen og driftsomkostninger. Ingen retroaktiv prisstigning.</span>
              </label>
            </div>
            <button type="submit" disabled={submitting}
              className="w-full sm:w-auto bg-brand-red text-white font-bold uppercase tracking-wide px-8 py-4 disabled:opacity-50"
              data-testid="sign-submit">
              {submitting ? "Underskriver..." : "Underskriv digitalt"}
            </button>
            <p className="text-xs text-white/60 mt-3">
              Ved at klikke &laquo;Underskriv digitalt&raquo; bekræfter du, at du har bemyndigelse til at indgå aftaler på vegne af {data.company_name}. Din IP og tidspunkt logges som signatur-bevis.
            </p>
          </form>
        </>
      )}
    </Shell>
  );
}

const RowK = ({ k, v }) => (
  <tr className="border-b border-stone-200 last:border-0">
    <td className="py-2 pr-4 text-stone-500 align-top w-1/3">{k}</td>
    <td className="py-2 text-brand-dark">{v}</td>
  </tr>
);

const Shell = ({ children }) => (
  <div className="min-h-screen bg-white">
    <div className="max-w-3xl mx-auto px-6 py-10">{children}</div>
  </div>
);
